/**
 * LDS order baggage display breakdown (read-only).
 * Does not alter totals, pricing, or ledger amounts.
 */
import {
  resolveLdsOrderFinalPriceBreakdown,
} from "../extractLdsOrderFinalPriceIls.js";
import { resolveLdsOrderPageDisplayTotalSsot } from "./ldsOrderPageDisplayTotalSsot.js";
import {
  buildOrderPassengerDisplayName,
  normalizeOrderPassengersInfoSlots,
} from "./resolveOrderPassengersByRooms.js";

export const LDS_BAGGAGE_BREAKDOWN_TRACE_TAG = "[LDS_BAGGAGE_BREAKDOWN_TRACE]";

const MONEY_TOL = 0.02;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function positive(value) {
  const n = toNumberOrNull(value);
  return n != null && n > MONEY_TOL ? n : null;
}

function isTrueFlag(value) {
  if (value === true) return true;
  if (typeof value === "number") return value > 0;
  if (typeof value !== "string") return false;
  const t = value.trim().toLowerCase();
  return t === "true" || t === "1" || t === "yes";
}

function tryParseJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function resolveOrderResponseObject(order) {
  const raw = order?.order_response;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  return tryParseJson(raw);
}

function classifyBaggageType(row) {
  const type = String(row?.type || row?.category || "")
    .trim()
    .toUpperCase();
  const desc = String(row?.description || row?.desc || row?.name || row?.title || "")
    .trim()
    .toUpperCase();
  if (type.includes("TROLLEY") || desc.includes("TROLLEY")) return "trolley";
  if (
    type.includes("CARRY") ||
    desc.includes("CARRY") ||
    desc.includes("HAND BAG") ||
    desc.includes("CABIN")
  ) {
    return "carry_on";
  }
  if (
    type.includes("LUGGAGE") ||
    type.includes("BAGGAGE") ||
    type.includes("XBAG") ||
    desc.includes("LUGGAGE") ||
    desc.includes("BAGGAGE") ||
    desc.includes("מזוודה")
  ) {
    return "luggage";
  }
  return null;
}

function resolveDirection(row) {
  const fltRef = String(row?.fltRef ?? row?.flight?.fltRef ?? "").trim();
  const text = String(row?.description || row?.desc || row?.name || "")
    .toUpperCase()
    .trim();
  if (
    text.includes("ROUNDTRIP") ||
    text.includes("ROUND TRIP") ||
    text.includes("BOTH") ||
    text.includes("RETURN + DEPARTURE") ||
    text.includes("הלוך חזור") ||
    text.includes("הלוך וחזור")
  ) {
    return "roundtrip";
  }
  if (text.includes("RETURN") || text.includes("INBOUND") || text.includes("חזור")) {
    return "return";
  }
  if (text.includes("DEPARTURE") || text.includes("OUTBOUND") || text.includes("הלוך")) {
    return "outbound";
  }
  if (fltRef === "1") return "outbound";
  if (fltRef === "2") return "return";
  if (fltRef === "-3") return "roundtrip";
  return "unknown";
}

function directionLabelHe(direction) {
  if (direction === "outbound") return "הלוך";
  if (direction === "return") return "חזור";
  if (direction === "roundtrip") return "הלוך חזור";
  return "";
}

function typeLabelHe(type) {
  if (type === "trolley") return "טרולי";
  if (type === "luggage") return "מזוודה";
  if (type === "carry_on") return "כבודת יד";
  return "כבודה";
}

function isRowSelected(row) {
  if (!row || typeof row !== "object") return false;
  return (
    isTrueFlag(row.checked) ||
    isTrueFlag(row.selected) ||
    isTrueFlag(row.isSelected)
  );
}

function rowAmountIls(row) {
  return (
    positive(row?.ourFare ?? row?.fare ?? row?.selectedTotalFare ?? row?.amountTotal) ??
    null
  );
}

function flattenSupplementEntry(entry, sourcePrefix) {
  if (!entry || typeof entry !== "object") return null;
  const inner = entry.orig?.supp ?? entry.supp ?? entry;
  const merged = {
    ...inner,
    checked: entry.checked ?? inner?.checked,
    selected: entry.selected ?? inner?.selected,
    pax: entry.pax ?? inner?.pax,
    paxRef:
      inner?.paxRef ??
      entry.paxRef ??
      entry.chosenPaxRef ??
      inner?.chosenPaxRef ??
      entry.pax?.["@seqNum"] ??
      entry.pax?.seqNum,
    fltRef: inner?.fltRef ?? entry.fltRef ?? entry.flight?.fltRef,
    ourFare: entry.ourFare ?? inner?.ourFare ?? inner?.fare,
    fare: entry.fare ?? inner?.fare,
    type: inner?.type ?? entry.type,
    description: inner?.description ?? entry.description ?? inner?.desc,
    vpBaggagePriceTrace:
      entry.vpBaggagePriceTrace ?? inner?.vpBaggagePriceTrace ?? null,
  };
  const baggageType = classifyBaggageType(merged);
  if (!baggageType) return null;
  return {
    row: merged,
    baggageType,
    source: sourcePrefix,
  };
}

/**
 * Pick one canonical supplement payload (same priority as pricing SSOT).
 * Campaign hotel_flight orders often mirror the same checked bags under
 * both order.supplement and order.flights.supplement — merging both would
 * double RT consolidation and show LUGGAGE x4 for two passengers.
 */
function supplementPayloadHasEntries(payload) {
  if (!payload || typeof payload !== "object") return false;
  const entries = Array.isArray(payload) ? payload : Object.values(payload);
  return entries.some((entry) => entry != null && typeof entry === "object");
}

function resolveCanonicalSupplementPayload(order) {
  const candidates = [];
  const push = (payload, source) => {
    if (!supplementPayloadHasEntries(payload)) return;
    candidates.push({ payload, source });
  };

  push(order?.supplement, "order.supplement");
  const flights = order?.flights;
  if (flights && typeof flights === "object" && !Array.isArray(flights)) {
    push(flights?.supplement, "order.flights.supplement");
    push(flights?.ourPrice?.supplement, "order.flights.ourPrice.supplement");
  }
  const or = resolveOrderResponseObject(order);
  if (or) {
    push(or?.supplement, "order.order_response.supplement");
    push(or?.flights?.supplement, "order.order_response.flights.supplement");
    push(or?.reservation?.supplement, "order.order_response.reservation.supplement");
    push(or?.reservation?.flight?.supplement, "order.order_response.reservation.flight.supplement");
  }
  const extBlocks = [];
  if (Array.isArray(order?.extended)) extBlocks.push(...order.extended);
  if (order?.extended && typeof order.extended === "object" && !Array.isArray(order.extended)) {
    extBlocks.push(order.extended);
  }
  if (Array.isArray(or?.extended)) extBlocks.push(...or.extended);
  for (let i = 0; i < extBlocks.length; i++) {
    const block = extBlocks[i];
    if (!block || typeof block !== "object") continue;
    push(block?.selectedRoom?.supplement, `order.extended[${i}].selectedRoom.supplement`);
    push(block?.supplement, `order.extended[${i}].supplement`);
  }

  return candidates[0] || { payload: null, source: null };
}

function collectSupplementRows(order) {
  const out = [];
  const { payload, source } = resolveCanonicalSupplementPayload(order);
  if (!payload) return out;

  const entries = Array.isArray(payload) ? payload : Object.values(payload);
  for (const entry of entries) {
    const flat = flattenSupplementEntry(entry, source || "order.supplement");
    if (flat) out.push(flat);
  }

  return out;
}

function buildPassengerNameIndex(order) {
  const index = new Map();
  const slots = normalizeOrderPassengersInfoSlots(order);
  let seq = 1;
  for (const slot of slots) {
    const room = slot?.room && typeof slot.room === "object" ? slot.room : slot;
    for (const key of ["adults", "children", "infants"]) {
      const list = room?.[key];
      if (!Array.isArray(list)) continue;
      for (const pax of list) {
        const name = buildOrderPassengerDisplayName(pax);
        const keys = [
          String(seq),
          String(pax?.["@seqNum"] ?? ""),
          String(pax?.seqNum ?? ""),
          String(pax?.paxRef ?? ""),
          String(pax?.id ?? ""),
        ].filter((k) => k && k !== "undefined");
        for (const k of keys) {
          if (name) index.set(k, name);
        }
        seq += 1;
      }
    }
  }
  return index;
}

function resolvePassengerName(order, row, paxIndex) {
  const paxRef = String(
    row?.paxRef ??
      row?.pax?.["@seqNum"] ??
      row?.pax?.seqNum ??
      row?.chosenPaxRef ??
      ""
  ).trim();
  if (paxRef && paxRef !== "-3" && paxIndex.has(paxRef)) {
    return paxIndex.get(paxRef);
  }
  const fromRow = buildOrderPassengerDisplayName(row?.pax);
  if (fromRow) return fromRow;
  const desc = String(row?.description || row?.desc || "").trim();
  if (desc && !/^(TROLLEY|LUGGAGE|BAGGAGE)/i.test(desc)) return desc;
  return paxRef && paxRef !== "-3" ? `נוסע ${paxRef}` : "נוסע";
}

function computeFallbackBaggageIls(order) {
  const priceBreakdown = resolveLdsOrderFinalPriceBreakdown(order);
  const totalSsot = resolveLdsOrderPageDisplayTotalSsot(order, { emitTrace: false });
  const resolved = positive(totalSsot.displayedTotal);
  const base = positive(priceBreakdown.baseBeforeBaggage);
  const finalAfter = positive(priceBreakdown.finalTotal);

  if (resolved != null && base != null) {
    const delta = round2(resolved - base);
    if (delta > MONEY_TOL) {
      return {
        amount: delta,
        source: "fallback:resolved_display_total_minus_base_before_baggage",
      };
    }
  }

  if (
    finalAfter != null &&
    base != null &&
    (priceBreakdown.baggageAddedOnce || priceBreakdown.baggageTotalDetected > MONEY_TOL)
  ) {
    const delta = round2(finalAfter - base);
    if (delta > MONEY_TOL) {
      return {
        amount: delta,
        source: "fallback:final_total_minus_base_before_baggage",
      };
    }
  }

  const bagPrice = positive(
    order?.ourprice?.baggagePrice ??
      order?.ourPrice?.baggagePrice ??
      order?.public_baggage_total_ils
  );
  if (bagPrice != null) {
    return { amount: round2(bagPrice), source: "fallback:ourprice.baggagePrice" };
  }

  return { amount: null, source: "none" };
}

/**
 * A provider may store the same checked bag as one supplement row per flight
 * leg. Pair matching outbound/return rows (or explicitly round-trip rows with
 * distinct flight refs) into one display item while retaining both prices.
 */
function canPairRoundTripLegRows(first, candidate) {
  const refA = String(first?.flightRef ?? "").trim();
  const refB = String(candidate?.flightRef ?? "").trim();
  if (refA && refB && refA !== refB) return true;
  const sharedLegRef = refA && refB && refA === refB;
  const sentinelLegRef = refA === "-3" || refB === "-3";
  const missingLegRef = !refA || !refB;
  if (sharedLegRef && !sentinelLegRef) return false;
  if (missingLegRef || sentinelLegRef || sharedLegRef) {
    return Math.abs(Number(first.amountIls) - Number(candidate.amountIls)) <= MONEY_TOL;
  }
  return false;
}

function consolidateRoundTripBaggageItems(items) {
  const grouped = new Map();
  for (const item of items) {
    const passengerKey = item.passengerRef || item.passengerName || "נוסע";
    const key = `${passengerKey}::${item.type}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(item);
  }

  const combined = [];
  const combine = (first, second) => ({
    ...first,
    direction: "roundtrip",
    directionLabelHe: directionLabelHe("roundtrip"),
    amountIls: round2(first.amountIls + second.amountIls),
    source: `${first.source}|${second.source}`,
    flightRef: `${first.flightRef || ""}|${second.flightRef || ""}`,
  });

  for (const group of grouped.values()) {
    const remainingOutbound = group.filter((item) => item.direction === "outbound");
    const remainingReturn = group.filter((item) => item.direction === "return");
    const explicitRoundTrip = group.filter((item) => item.direction === "roundtrip");
    const unpaired = group.filter(
      (item) =>
        item.direction !== "outbound" &&
        item.direction !== "return" &&
        item.direction !== "roundtrip"
    );

    while (remainingOutbound.length && remainingReturn.length) {
      combined.push(combine(remainingOutbound.shift(), remainingReturn.shift()));
    }
    combined.push(...remainingOutbound, ...remainingReturn);

    // Suppliers often label each leg "מזוודה הלוך חזור" with fltRef 1/2 or -3.
    // Pair different legs first; then collapse equal half-fare rows (typical leg split).
    while (explicitRoundTrip.length) {
      const first = explicitRoundTrip.shift();
      const pairIndex = explicitRoundTrip.findIndex((candidate) =>
        canPairRoundTripLegRows(first, candidate)
      );
      if (pairIndex === -1) {
        combined.push(first);
      } else {
        combined.push(combine(first, explicitRoundTrip.splice(pairIndex, 1)[0]));
      }
    }
    combined.push(...unpaired);
  }

  return combined;
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

function emitBaggageTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info(LDS_BAGGAGE_BREAKDOWN_TRACE_TAG, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} order
 * @param {{ emitTrace?: boolean }} [options]
 */
export function resolveLdsBaggageBreakdown(order, options = {}) {
  const empty = {
    totalBaggageAmountIls: 0,
    items: [],
    source: "none",
    fallbackUsed: false,
    fallbackLabel: null,
    passengersGrouped: [],
  };

  if (!order || typeof order !== "object") {
    return empty;
  }

  const paxIndex = buildPassengerNameIndex(order);
  const supplementRows = collectSupplementRows(order);
  const items = [];

  for (const { row, baggageType, source } of supplementRows) {
    if (!isRowSelected(row)) continue;
    const amountIls = rowAmountIls(row);
    if (amountIls == null) continue;
    const direction = resolveDirection(row);
    items.push({
      passengerName: resolvePassengerName(order, row, paxIndex),
      passengerRef: String(
        row?.paxRef ?? row?.pax?.["@seqNum"] ?? row?.pax?.seqNum ?? ""
      ).trim(),
      type: baggageType,
      typeLabelHe: typeLabelHe(baggageType),
      direction,
      directionLabelHe: directionLabelHe(direction),
      amountIls: round2(amountIls),
      source,
      flightRef: String(row?.fltRef ?? row?.flight?.fltRef ?? "").trim(),
    });
  }

  const displayItems = consolidateRoundTripBaggageItems(items);
  let fallbackUsed = false;
  let source = "order.supplement";
  let totalBaggageAmountIls = round2(
    displayItems.reduce((sum, it) => sum + (it.amountIls || 0), 0)
  );

  if (displayItems.length === 0) {
    const fb = computeFallbackBaggageIls(order);
    if (fb.amount != null) {
      fallbackUsed = true;
      totalBaggageAmountIls = fb.amount;
      source = fb.source;
    }
  } else if (totalBaggageAmountIls <= MONEY_TOL) {
    const fb = computeFallbackBaggageIls(order);
    if (fb.amount != null) {
      fallbackUsed = true;
      totalBaggageAmountIls = fb.amount;
      source = `${source}|${fb.source}`;
    } else {
      source = displayItems[0]?.source || source;
    }
  } else {
    source = "order.supplement.per_passenger";
  }

  const passengersGrouped = [];
  const byPax = new Map();
  for (const item of displayItems) {
    const key = item.passengerName || "נוסע";
    if (!byPax.has(key)) {
      byPax.set(key, { passengerName: key, items: [], totalIls: 0 });
    }
    const g = byPax.get(key);
    g.items.push(item);
    g.totalIls = round2(g.totalIls + item.amountIls);
  }
  for (const g of byPax.values()) {
    passengersGrouped.push(g);
  }

  const out = {
    totalBaggageAmountIls: totalBaggageAmountIls > MONEY_TOL ? totalBaggageAmountIls : 0,
    items: displayItems,
    source,
    fallbackUsed,
    fallbackLabel: fallbackUsed
      ? `כבודה ותוספות: ₪${Math.ceil(totalBaggageAmountIls - 1e-9).toLocaleString("en-US")}`
      : null,
    passengersGrouped,
  };

  if (options.emitTrace !== false) {
    emitBaggageTrace({
      orderId: String(order.id ?? order._id ?? order.orderid ?? ""),
      totalBaggageAmountIls: out.totalBaggageAmountIls,
      itemsCount: out.items.length,
      fallbackUsed: out.fallbackUsed,
      source: out.source,
    });
  }

  return out;
}

/** Selected baggage supplement rows from the canonical payload (no round-trip merge). */
export function listSelectedLdsBaggageSupplementRows(order) {
  if (!order || typeof order !== "object") return [];
  return collectSupplementRows(order)
    .filter(({ row }) => isRowSelected(row))
    .map(({ row }) => row);
}

function baggageSummaryTypeLabel(item) {
  if (item?.type === "luggage") return "LUGGAGE";
  if (item?.type === "trolley") return "TROLLEY";
  if (item?.type === "carry_on") return "CARRY_ON";
  return String(item?.type || "baggage").toUpperCase();
}

/** @param {ReturnType<typeof resolveLdsBaggageBreakdown>} breakdown */
export function buildLdsBaggageSummaryEntries(breakdown) {
  const counts = new Map();
  for (const item of breakdown?.items ?? []) {
    const label = baggageSummaryTypeLabel(item);
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return [...counts.entries()];
}

/** @param {[string, number][]} entries */
export function formatLdsBaggageSummaryDisplay(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return "";
  return entries.map(([type, count]) => `${type} x${count}`).join(", ");
}

/** @param {ReturnType<typeof resolveLdsBaggageBreakdown>} breakdown */
export function formatBaggageBreakdownLineLabel(item) {
  const typeHe = item.typeLabelHe || typeLabelHe(item.type);
  const dirHe = item.directionLabelHe || directionLabelHe(item.direction);
  const dirPart = dirHe ? ` ${dirHe}` : "";
  return `${typeHe}${dirPart}`;
}
